import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  RefreshCw,
  Target,
  ScanLine,
  Clock,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { diagnosticsApi, ApiError } from '../../lib/api';

interface FailingCriterion {
  field: string;
  operator: string;
  expected_value: number | null;
  expected_value2: number | null;
  failures: number;
  near_misses: number;
  fail_rate: number;
  near_miss_rate: number;
  typical_actual: number | null;
}

interface NearMissCriterion {
  field: string;
  operator: string;
  expected_value: number | null;
  expected_value2: number | null;
  actual_value: number | null;
  passed: boolean;
  is_near_miss: boolean;
  distance_pct: number | null;
}

interface NearMissSample {
  symbol: string;
  timestamp: string;
  criteria_passed: number;
  criteria_total: number;
  near_miss_count: number;
  criteria_results: NearMissCriterion[];
}

interface PatternForensics {
  pattern_id: string;
  pattern_name: string;
  is_active: boolean;
  asset_class: string;
  eval_count: number;
  match_count: number;
  near_miss_count: number;
  match_rate: number;
  near_miss_rate: number;
  last_eval_ts: string | null;
  last_signal_ts: string | null;
  top_failing_criteria: FailingCriterion[];
  recent_near_misses: NearMissSample[];
  total_criteria_on_pattern: number;
  diagnosis: string;
}

interface ScanLoopSummary {
  count: number;
  note?: string;
  last_scan_ts?: string;
  last_universe_size?: number;
  last_filtered_size?: number;
  last_patterns_scanned?: number;
  last_total_evaluations?: number;
  last_matches?: number;
  last_signals_generated?: number;
  last_session?: string;
  avg_duration_ms?: number;
  total_evaluations_window?: number;
  total_matches_window?: number;
  total_signals_window?: number;
}

interface ForensicsReport {
  window_hours: number;
  generated_at: string;
  active_patterns_count: number;
  patterns_with_activity: number;
  patterns_silent: number;
  total_evaluations: number;
  total_matches: number;
  scan_loop_summary: ScanLoopSummary;
  patterns: PatternForensics[];
}

const fmtRelative = (iso: string | null | undefined): string => {
  if (!iso) return 'never';
  try {
    const ts = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - ts);
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ${min % 60}m ago`;
    return `${Math.floor(hr / 24)}d ago`;
  } catch {
    return 'unknown';
  }
};

const pctStr = (v: number): string => {
  if (v == null || Number.isNaN(v)) return '—';
  return `${(v * 100).toFixed(1)}%`;
};

const describeOperator = (op: string, expected: number | null, expected2: number | null): string => {
  if (expected == null) return op;
  const e = Number(expected).toLocaleString(undefined, { maximumFractionDigits: 4 });
  switch ((op || '').toLowerCase()) {
    case 'gt':
    case '>':
      return `> ${e}`;
    case 'gte':
    case '>=':
      return `≥ ${e}`;
    case 'lt':
    case '<':
      return `< ${e}`;
    case 'lte':
    case '<=':
      return `≤ ${e}`;
    case 'eq':
    case '==':
      return `= ${e}`;
    case 'between':
      return `between ${e} and ${expected2 != null ? Number(expected2).toLocaleString() : '?'}`;
    default:
      return `${op} ${e}`;
  }
};

const diagnosisBadge = (diag: string) => {
  if (diag.startsWith('silent')) return 'bg-rose-500/10 text-rose-700 border-rose-500/20';
  if (diag.startsWith('criteria likely too strict')) return 'bg-amber-500/10 text-amber-700 border-amber-500/20';
  if (diag.startsWith('close but no hits')) return 'bg-amber-500/10 text-amber-700 border-amber-500/20';
  if (diag.startsWith('matched but no signal')) return 'bg-amber-500/10 text-amber-700 border-amber-500/20';
  if (diag.startsWith('healthy')) return 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20';
  return 'bg-muted text-muted-foreground border-muted';
};

const BotDecisions: React.FC = () => {
  const [hours, setHours] = useState(24);
  const [report, setReport] = useState<ForensicsReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [evalRate, setEvalRate] = useState<number | null>(null);
  const [evalRateSaving, setEvalRateSaving] = useState(false);
  const [evalRateLoadError, setEvalRateLoadError] = useState(false);
  // Counter to discard stale update responses when requests overlap.
  const evalRateSeq = useRef(0);

  const toggleExpanded = (pid: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await diagnosticsApi.botDecisions(hours);
      setReport(data?.data || null);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : err instanceof Error ? err.message : 'Failed to load forensics');
    } finally {
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const fetchEvalRate = useCallback(async () => {
    try {
      const data = await diagnosticsApi.evalSampleRate() as any;
      setEvalRate(data?.data?.rate ?? 0.1);
      setEvalRateLoadError(false);
    } catch {
      setEvalRateLoadError(true);
    }
  }, []);

  useEffect(() => { fetchEvalRate(); }, [fetchEvalRate]);

  const updateEvalRate = async (rate: number) => {
    // Stamp this request so a stale response from a previous in-flight call
    // can't overwrite the value the user most recently chose.
    evalRateSeq.current += 1;
    const seq = evalRateSeq.current;
    setEvalRateSaving(true);
    try {
      const data = await diagnosticsApi.setEvalSampleRate(rate) as any;
      if (seq === evalRateSeq.current) {
        setEvalRate(data?.data?.rate ?? rate);
      }
    } catch {
      // swallow — UI stays on previous value
    } finally {
      if (seq === evalRateSeq.current) {
        setEvalRateSaving(false);
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* Controls */}
      <Card>
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Activity className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="text-sm font-semibold">Bot Decisions</div>
              <div className="text-xs text-muted-foreground">
                Per-pattern evaluation forensics — why bots are (or aren't) signalling
              </div>
            </div>
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground whitespace-nowrap" title="Fraction of non-match evaluations that get persisted to Firestore. Matches and near-misses are always logged. 1.0 = full debug visibility; 0.1 = default sampling.">
              Eval sampling:
            </label>
            {evalRateLoadError && (
              <span className="text-xs text-destructive" title="Failed to load eval rate — click refresh to retry">
                load failed
              </span>
            )}
            <Select
              value={evalRate != null ? String(evalRate) : '0.1'}
              onValueChange={(v) => updateEvalRate(Number(v))}
              disabled={evalRateSaving || (evalRate == null && !evalRateLoadError)}
            >
              <SelectTrigger className="w-[170px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Match + near-miss only</SelectItem>
                <SelectItem value="0.1">Default (10%)</SelectItem>
                <SelectItem value="0.25">Extended (25%)</SelectItem>
                <SelectItem value="0.5">High (50%)</SelectItem>
                <SelectItem value="1">Full debug (100%)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Select value={String(hours)} onValueChange={(v) => setHours(Number(v))}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 1 hour</SelectItem>
              <SelectItem value="4">Last 4 hours</SelectItem>
              <SelectItem value="24">Last 24 hours</SelectItem>
              <SelectItem value="72">Last 3 days</SelectItem>
              <SelectItem value="168">Last 7 days</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchReport} disabled={loading} size="sm">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardContent>
        {evalRate != null && evalRate >= 0.5 && (
          <div className="px-4 pb-4">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Debug sampling at {(evalRate * 100).toFixed(0)}% — Firestore write volume will be
                roughly {(evalRate / 0.1).toFixed(0)}× normal. Drop back to Default when you're done
                diagnosing to keep costs in check.
              </AlertDescription>
            </Alert>
          </div>
        )}
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading && !report && (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      )}

      {report && (
        <>
          {/* Scan-loop health strip */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <ScanLine className="h-4 w-4 text-primary" />
                Scanner health
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Scan loops</div>
                <div className="text-xl font-semibold">{report.scan_loop_summary.count}</div>
                <div className="text-xs text-muted-foreground">
                  last: {fmtRelative(report.scan_loop_summary.last_scan_ts || null)}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Evals (window)</div>
                <div className="text-xl font-semibold">
                  {report.scan_loop_summary.total_evaluations_window ?? 0}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Matches (window)</div>
                <div className="text-xl font-semibold">
                  {report.scan_loop_summary.total_matches_window ?? 0}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Signals (window)</div>
                <div className="text-xl font-semibold">
                  {report.scan_loop_summary.total_signals_window ?? 0}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Active bots</div>
                <div className="text-xl font-semibold">{report.active_patterns_count}</div>
                <div className="text-xs text-muted-foreground">
                  {report.patterns_silent} silent, {report.patterns_with_activity} evaluating
                </div>
              </div>
            </CardContent>
            {report.scan_loop_summary.note && (
              <div className="px-4 pb-4">
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{report.scan_loop_summary.note}</AlertDescription>
                </Alert>
              </div>
            )}
          </Card>

          {/* Per-pattern forensics */}
          {report.patterns.length === 0 && (
            <Card>
              <CardContent className="p-6 text-center text-sm text-muted-foreground">
                No patterns or eval logs in this window.
              </CardContent>
            </Card>
          )}

          {report.patterns.map((p) => {
            const isOpen = expanded.has(p.pattern_id);
            return (
              <Card key={p.pattern_id || p.pattern_name}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Target className="h-4 w-4 text-primary" />
                        {p.pattern_name || p.pattern_id || '(unnamed)'}
                        {!p.is_active && (
                          <Badge variant="secondary" className="text-xs">inactive</Badge>
                        )}
                        {p.asset_class && p.asset_class !== 'stocks' && (
                          <Badge variant="secondary" className="text-xs uppercase">
                            {p.asset_class}
                          </Badge>
                        )}
                      </CardTitle>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        <Clock className="inline h-3 w-3 mr-1" />
                        last eval: {fmtRelative(p.last_eval_ts)} · last signal:{' '}
                        {fmtRelative(p.last_signal_ts)}
                      </div>
                    </div>
                    <Badge variant="outline" className={`text-xs ${diagnosisBadge(p.diagnosis)}`}>
                      {p.diagnosis}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-xs text-muted-foreground">Evaluations</div>
                      <div className="text-lg font-semibold">{p.eval_count.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Matched</div>
                      <div className="text-lg font-semibold">{p.match_count.toLocaleString()}</div>
                      <div className="text-xs text-muted-foreground">
                        {pctStr(p.match_rate)} match rate
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Near-misses</div>
                      <div className="text-lg font-semibold">{p.near_miss_count.toLocaleString()}</div>
                      <div className="text-xs text-muted-foreground">
                        {pctStr(p.near_miss_rate)} of evals
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Criteria</div>
                      <div className="text-lg font-semibold">{p.total_criteria_on_pattern}</div>
                    </div>
                  </div>

                  {p.top_failing_criteria.length > 0 && (
                    <div className="mt-4">
                      <div className="text-xs font-semibold mb-1.5">Top failing criteria</div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Field</TableHead>
                            <TableHead>Required</TableHead>
                            <TableHead className="text-right">Typical actual</TableHead>
                            <TableHead className="text-right">Fail rate</TableHead>
                            <TableHead className="text-right">Near-miss</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {p.top_failing_criteria.map((c) => (
                            <TableRow key={c.field}>
                              <TableCell className="font-mono text-xs">{c.field}</TableCell>
                              <TableCell className="text-xs">
                                {describeOperator(c.operator, c.expected_value, c.expected_value2)}
                              </TableCell>
                              <TableCell className="text-right font-mono text-xs">
                                {c.typical_actual == null ? '—' : c.typical_actual}
                              </TableCell>
                              <TableCell className="text-right text-xs">
                                {pctStr(c.fail_rate)}
                              </TableCell>
                              <TableCell className="text-right text-xs">
                                {pctStr(c.near_miss_rate)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  {p.recent_near_misses.length > 0 && (
                    <div className="mt-4">
                      <button
                        className="text-xs text-primary hover:underline"
                        onClick={() => toggleExpanded(p.pattern_id)}
                      >
                        {isOpen ? 'Hide' : 'Show'} {p.recent_near_misses.length} recent near-miss evaluations
                      </button>
                      {isOpen && (
                        <div className="mt-2 space-y-2">
                          {p.recent_near_misses.map((nm, i) => (
                            <div key={i} className="border rounded-md p-2 bg-muted/20">
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="font-semibold">{nm.symbol}</span>
                                <span className="text-muted-foreground">
                                  {fmtRelative(nm.timestamp)} · {nm.criteria_passed}/
                                  {nm.criteria_total} passed
                                </span>
                              </div>
                              <div className="space-y-0.5">
                                {nm.criteria_results.map((c, j) => (
                                  <div
                                    key={j}
                                    className={`flex items-center justify-between text-xs font-mono ${
                                      c.passed
                                        ? 'text-emerald-700'
                                        : c.is_near_miss
                                          ? 'text-amber-700'
                                          : 'text-rose-700'
                                    }`}
                                  >
                                    <span>
                                      {c.passed ? '✓' : c.is_near_miss ? '≈' : '✗'} {c.field}
                                    </span>
                                    <span>
                                      req {describeOperator(c.operator, c.expected_value, c.expected_value2)}
                                      {' · got '}
                                      {c.actual_value == null ? '—' : c.actual_value}
                                      {c.distance_pct != null && !c.passed
                                        ? ` (${c.distance_pct > 0 ? '+' : ''}${c.distance_pct.toFixed(1)}%)`
                                        : ''}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </>
      )}
    </div>
  );
};

export default BotDecisions;
